import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { loadOptionList, optionLabels } from "@/lib/options";
import { useAuth } from "@/context/AuthContext";
import { Card, EmptyState, Field } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, Loader2, CheckCircle2, XCircle, Send, ChevronsUpDown, FolderOpen, AlertCircle, Trash2, IdCard } from "lucide-react";
import { CC_ROLES } from "@/lib/constants";
import { fmtDate } from "@/lib/dates";
import { StudentFiles } from "@/components/StudentFiles";


const DENIAL_REASON_FALLBACK = ["No Clinical Instructor Available", "Capacity Full", "Staffing Shortage", "Dates Unavailable", "Discipline Not Supported", "Location Not Appropriate", "Future Date Too Far Away", "School Cancelled", "Student Cancelled", "Unable to Accommodate", "Other"];
const CI_DISCIPLINES = ["PT", "OT", "SLP", "PTA", "OTA"];

export default function PlacementDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pr, setPr] = useState(null);
  const [matchOpen, setMatchOpen] = useState(false);
  const [matches, setMatches] = useState([]);
  const [selected, setSelected] = useState({});
  const [respondFor, setRespondFor] = useState(null);
  const [resp, setResp] = useState({ response_status: "Accepted" });
  const [facilityCIs, setFacilityCIs] = useState([]);
  const [filesOpen, setFilesOpen] = useState(false);
  const [denialReasons, setDenialReasons] = useState(DENIAL_REASON_FALLBACK);
  useEffect(() => {
    loadOptionList("denial_reasons").then((opts) => { if (opts.length) setDenialReasons(optionLabels(opts)); }).catch(() => {});
  }, []);
  const [addingCi, setAddingCi] = useState(false);
  const [newCi, setNewCi] = useState({});
  const loadCIs = (fid) => api.get("/clinical-instructors", { params: { facility_id: fid } }).then(({ data }) => setFacilityCIs(data)).catch(() => setFacilityCIs([]));
  const addNewCi = async () => {
    if (!newCi.name || !newCi.email) return toast.error("Instructor name and email are required");
    try {
      const { data } = await api.post("/clinical-instructors", {
        name: newCi.name, email: newCi.email, phone: newCi.phone,
        disciplines: newCi.discipline ? [newCi.discipline] : [], facility_id: respondFor.facility_id,
      });
      toast.success("Instructor added");
      await loadCIs(respondFor.facility_id);
      setResp((r) => ({ ...r, clinical_instructor_id: data.id }));
      setAddingCi(false); setNewCi({});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => {
    if (respondFor?.facility_id) { loadCIs(respondFor.facility_id); setAddingCi(false); setNewCi({}); }
  }, [respondFor]);
  const [msg, setMsg] = useState("");
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignFacilityId, setAssignFacilityId] = useState("");
  const [facPickerOpen, setFacPickerOpen] = useState(false);
  const [finalNoOpen, setFinalNoOpen] = useState(false);
  const [finalNoReason, setFinalNoReason] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [welcome, setWelcome] = useState({});
  const [adhocOpen, setAdhocOpen] = useState(false);
  const [adhoc, setAdhoc] = useState({ name: "", description: "", required: false, due_date: "", doc_type: "form" });
  const [adhocSaving, setAdhocSaving] = useState(false);
  const [clearing, setClearing] = useState(false);

  const isCC = CC_ROLES.includes(user.role);
  // The DOR answers Yes/No for their site — same actions as the CI.
  const isFacility = ["Clinical Instructor", "DOR"].includes(user.role);
  const isDOR = user.role === "DOR";
  const isStudent = user.role === "Student";
  const canEditWelcome = ["Super Admin", "Campus Coordinator", "Campus Coordinator Supervisor", "Clinical Instructor"].includes(user.role);

  const load = useCallback(() => api.get(`/placement-requests/${id}`).then(({ data }) => setPr(data)), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const a = pr?.responses?.find((r) => r.response_status === "Accepted");
    if (a) setWelcome({ report_location: a.report_location || "", arrival_time: a.arrival_time || "", dress_code: a.dress_code || "", parking_instructions: a.parking_instructions || "", entrance_instructions: a.entrance_instructions || "", ci_phone: a.ci_phone || "", ci_notes: a.ci_notes || "" });
  }, [pr]);

  if (!pr) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-cc-blue" /></div>;

  const openMatch = async () => {
    const { data } = await api.post(`/placement-requests/${id}/match`, {});
    setMatches(data.facilities); setMatchOpen(true);
  };
  const notify = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!ids.length) return toast.error("Select at least one facility");
    await api.post(`/placement-requests/${id}/notify-facilities`, { facility_ids: ids });
    toast.success(`Notified ${ids.length} facilities`); setMatchOpen(false); setSelected({}); load();
  };
  const openAssign = async () => {
    const { data } = await api.get(`/facilities/options`);
    setMatches(data); setAssignFacilityId(""); setAssignOpen(true);
  };
  const doAssign = async () => {
    if (!assignFacilityId) return toast.error("Select the facility the school requested");
    try {
      const { data } = await api.post(`/placement-requests/${id}/assign-facility`, { facility_id: assignFacilityId });
      toast.success(data.dor_emailed ? "Facility assigned — DOR emailed for Yes/No" : "Facility assigned (no DOR email on file)");
      setAssignOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doFinalNo = async () => {
    try {
      await api.post(`/placement-requests/${id}/final-no`, { reason: finalNoReason });
      toast.success("Marked Campus Coordinator Final No"); setFinalNoOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doCancel = async () => {
    try {
      await api.post(`/placement-requests/${id}/cancel`, { reason: cancelReason });
      toast.success("Placement cancelled — all parties notified"); setCancelOpen(false); setCancelReason(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveWelcome = async (sendEmail) => {
    try {
      const { data } = await api.post(`/placement-requests/${id}/welcome-packet`, { ...welcome, send_email: sendEmail });
      toast.success(sendEmail ? `Welcome packet emailed${data.emailed?.length ? " to " + data.emailed.length + " recipient(s)" : ""}` : "Welcome info saved");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const submitResponse = async () => {
    if (resp.response_status === "Accepted" && !resp.clinical_instructor_id) return toast.error("Assign a Clinical Instructor (or add a new one) before accepting.");
    if (resp.response_status === "Denied" && !resp.denial_reason) return toast.error("Please choose a reason.");
    try {
      await api.post(`/facility-responses/${respondFor.id}/respond`, resp);
      toast.success(resp.response_status === "Accepted" ? "Marked Yes — placement accepted" : "Marked No — coordinator notified"); setRespondFor(null); setResp({ response_status: "Accepted" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const requestCancel = async () => {
    const reason = window.prompt("Ask the Campus Coordinator to cancel this placement. Add a short reason (optional):", "");
    if (reason === null) return;
    try { await api.post(`/placement-requests/${id}/request-cancel`, { reason }); toast.success("Cancellation request sent to the coordinator"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reviewPaperwork = async (a, decision, reason = "") => {
    await api.post(`/paperwork-assignments/${a.id}/review`, { decision, rejection_reason: reason });
    toast.success(`${a.name} ${decision}`); load();
  };
  const addAdhoc = async () => {
    if (!adhoc.name.trim()) return toast.error("Document name is required");
    setAdhocSaving(true);
    try {
      await api.post(`/placement-requests/${id}/paperwork`, adhoc);
      toast.success(`"${adhoc.name}" added to the packet`);
      setAdhocOpen(false); setAdhoc({ name: "", description: "", required: false, due_date: "", doc_type: "form" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAdhocSaving(false); }
  };
  const fileHref = (loc) => (loc?.startsWith("http") ? loc : `${process.env.REACT_APP_BACKEND_URL}${loc}`);
  const openFolder = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/placement-requests/${id}/paperwork-folder/open`, "_blank", "noopener");
  };
  const submitPaperwork = async (a, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("notes", "");
      await api.post(`/paperwork-assignments/${a.id}/submit`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${a.name} uploaded & submitted`); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const assignPacket = async () => {
    try {
      const { data } = await api.post(`/placement-requests/${id}/assign-packet`);
      toast.success(`${data.count} documents assigned — the student can see them now`); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const clearStudent = async () => {
    setClearing(true);
    try { await api.post(`/placement-requests/${id}/clear`); toast.success("Student cleared to start! All-clear emails and the calendar invite are on their way."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setClearing(false); }
  };
  const sendMsg = async () => {
    if (!msg.trim()) return;
    await api.post("/messages", { placement_request_id: id, message_body: msg });
    setMsg(""); load();
  };

  return (
    <div>
      <button onClick={() => navigate("/placements")} className="flex items-center gap-2 text-sm text-zinc-500 mb-4" data-testid="back-btn"><ArrowLeft className="w-4 h-4" /> Back to placements</button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900">{pr.student?.name}</h1>
          <p className="text-sm text-zinc-500">{pr.discipline} · {pr.rotation_level} · {pr.school?.name}</p>
          <div className="flex gap-2 mt-3"><StatusBadge status={pr.placement_status} /><StatusBadge status={pr.clearance_status} /></div>
        </div>
        <div className="flex flex-wrap gap-2">
          {isCC && !pr.accepted_facility_id && <Button onClick={openAssign} className="bg-cc-blue text-white" data-testid="assign-facility-btn">Assign Facility</Button>}
          {isCC && !pr.accepted_facility_id && <Button onClick={openMatch} variant="outline" data-testid="match-btn">Match Facilities</Button>}
          {isCC && !pr.accepted_facility_id && pr.assigned_facility_id && <Button onClick={() => setFinalNoOpen(true)} variant="outline" className="text-red-600 border-red-200" data-testid="final-no-btn">Mark Final No</Button>}
          {isCC && pr.clearance_status !== "Cleared to Start" && pr.accepted_facility_id && <Button onClick={clearStudent} disabled={clearing} className="bg-cc-green text-white" data-testid="clear-btn">{clearing ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Clearing…</> : "Clear to Start"}</Button>}
          {isCC && !["Cancelled", "Completed", "Campus Coordinator Final No"].includes(pr.placement_status) && <Button onClick={() => setCancelOpen(true)} variant="outline" className="text-red-600 border-red-200" data-testid="cancel-placement-btn">Cancel Placement</Button>}
          {isFacility && !["Cancelled", "Completed"].includes(pr.placement_status) && <Button onClick={requestCancel} variant="outline" className="text-red-600 border-red-200" data-testid="request-cancel-btn">Request Cancel</Button>}
          {pr.cancel_requested && <span className="text-xs text-amber-600 self-center" data-testid="cancel-requested-flag">Cancellation requested</span>}
        </div>
      </div>

      {pr.placement_status === "Cancelled" && (
        <div className="mb-6 rounded-lg border-l-4 border-red-500 bg-red-50 px-5 py-3" data-testid="cancellation-banner">
          <p className="font-semibold text-red-800">This placement was cancelled</p>
          {pr.cancellation_reason && <p className="text-sm text-red-700 mt-0.5">Reason: {pr.cancellation_reason}</p>}
        </div>
      )}

      {isCC && pr.placement_status === "Claim Pending Approval" && (() => {
        const claim = pr.responses?.find((r) => r.is_claim);
        const fac = claim ? { name: claim.facility_name } : null;
        const doClaim = async (action) => {
          try {
            await api.post(`/placement-requests/${id}/${action}-claim`);
            toast.success(action === "approve" ? "Claim approved" : "Claim rejected");
            load();
          } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        };
        return (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border-l-4 border-amber-500 bg-amber-50 px-5 py-4" data-testid="claim-approval-banner">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
              <div>
                <p className="font-semibold text-zinc-900">A facility claimed this student — your approval is needed</p>
                <p className="text-sm text-zinc-600">{fac?.name || "A facility"} wants to take this student. Approve to confirm the placement, or reject to keep it open.</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => doClaim("approve")} className="bg-cc-green text-white" data-testid="approve-claim-btn">Approve</Button>
              <Button onClick={() => doClaim("reject")} variant="outline" className="text-red-600 border-red-200" data-testid="reject-claim-btn">Reject</Button>
            </div>
          </div>
        );
      })()}

      {isFacility && (() => {
        const p = pr.responses?.find((r) => r.response_status === "Pending");
        return p ? (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border-l-4 border-cc-blue bg-cc-blue/5 px-5 py-4" data-testid="facility-action-banner">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-cc-blue shrink-0" />
              <div>
                <p className="font-semibold text-zinc-900">Action needed: respond to this placement request</p>
                <p className="text-sm text-zinc-600">Let the coordinator know whether your facility can accept this student.</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => { setRespondFor(p); setResp({ response_status: "Accepted", proposed_start_date: pr.requested_start_date, proposed_end_date: pr.requested_end_date }); }} className="bg-cc-green text-white" data-testid="banner-accept-btn">Mark Yes / No</Button>
              <Button onClick={() => { setRespondFor(p); setResp({ response_status: "Denied" }); }} variant="outline" className="text-red-600 border-red-200" data-testid="banner-decline-btn">Answer No</Button>
            </div>
          </div>
        ) : null;
      })()}

      <Tabs defaultValue="overview">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="student" data-testid="tab-student">Student Contact</TabsTrigger>
          <TabsTrigger value="facilities" data-testid="tab-facilities">Facility Responses</TabsTrigger>
          {!isDOR && <TabsTrigger value="paperwork" data-testid="tab-paperwork">Paperwork</TabsTrigger>}
          <TabsTrigger value="messages" data-testid="tab-messages">Messages</TabsTrigger>
          {!isDOR && <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>}
        </TabsList>

        <TabsContent value="overview">
          <div className="grid md:grid-cols-2 gap-6">
            <Card title="Placement Details">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <Info label="Request date" value={fmt(pr.request_date)} />
                <Info label="Campus city" value={pr.campus_city} />
                <Info label="Clinical Educator" value={pr.clinical_educator_name} />
                <Info label="Educator email" value={pr.clinical_educator_email} />
                <Info label="Program Director email" value={pr.program_director_email} />
                <Info label="Rotation level" value={pr.rotation_level} />
                <Info label="Facility" value={pr.facility?.name} />
                <Info label="Coordinator" value={pr.coordinator?.name} />
                <Info label="Clinical Instructor" value={pr.clinical_instructor?.name} />
                <Info label="Start" value={fmt(pr.accepted_start_date || pr.requested_start_date)} />
                <Info label="End" value={fmt(pr.accepted_end_date || pr.requested_end_date)} />
                <Info label="Graduation" value={fmt(pr.graduation_date)} />
                <Info label="State requested" value={pr.preferred_location_state} />
                <Info label="Notes" value={pr.notes} />
              </div>
            </Card>
            {!isDOR && <ProgramDocuments prId={pr.id} role={user?.role} />}
            <Card title="Student Welcome Packet">
              {!pr.responses?.find((r) => r.response_status === "Accepted") ? (
                <EmptyState text="Available once a facility accepts." />
              ) : canEditWelcome ? (
                <div className="space-y-3">
                  <p className="text-xs text-zinc-500">Clinical Instructor: add your demographics &amp; facility-specific info, then send the packet to the student and school.</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Report to"><Input data-testid="welcome-report" value={welcome.report_location || ""} onChange={(e) => setWelcome({ ...welcome, report_location: e.target.value })} /></Field>
                    <Field label="Arrival time"><Input data-testid="welcome-arrival" value={welcome.arrival_time || ""} onChange={(e) => setWelcome({ ...welcome, arrival_time: e.target.value })} /></Field>
                    <Field label="Dress code"><Input data-testid="welcome-dress" value={welcome.dress_code || ""} onChange={(e) => setWelcome({ ...welcome, dress_code: e.target.value })} /></Field>
                    <Field label="Parking"><Input data-testid="welcome-parking" value={welcome.parking_instructions || ""} onChange={(e) => setWelcome({ ...welcome, parking_instructions: e.target.value })} /></Field>
                    <Field label="Entrance"><Input data-testid="welcome-entrance" value={welcome.entrance_instructions || ""} onChange={(e) => setWelcome({ ...welcome, entrance_instructions: e.target.value })} /></Field>
                    <Field label="CI phone"><Input data-testid="welcome-ci-phone" value={welcome.ci_phone || ""} onChange={(e) => setWelcome({ ...welcome, ci_phone: e.target.value })} /></Field>
                  </div>
                  <Field label="Notes for student"><Textarea data-testid="welcome-notes" value={welcome.ci_notes || ""} onChange={(e) => setWelcome({ ...welcome, ci_notes: e.target.value })} /></Field>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => saveWelcome(false)} data-testid="welcome-save-btn">Save</Button>
                    <Button className="bg-cc-blue text-white" onClick={() => saveWelcome(true)} data-testid="welcome-send-btn">Save &amp; email student/school</Button>
                  </div>
                </div>
              ) : (
                (() => { const a = pr.responses.find((r) => r.response_status === "Accepted"); return (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <Info label="Report to" value={a.report_location} />
                    <Info label="Arrival time" value={a.arrival_time} />
                    <Info label="Dress code" value={a.dress_code} />
                    <Info label="Parking" value={a.parking_instructions} />
                    <Info label="Entrance" value={a.entrance_instructions} />
                    <Info label="CI phone" value={a.ci_phone} />
                  </div>
                ); })()
              )}
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="student">
          <StudentContact pr={pr} isDOR={isDOR} canOpenProfile={isCC} />
        </TabsContent>

        <TabsContent value="facilities">
          <Card>
            {pr.responses?.length === 0 ? <EmptyState text="No facilities notified yet." /> : (
              <div className="space-y-3">
                {pr.responses.map((r) => (
                  <div key={r.id} className="flex items-center justify-between border rounded-md p-4" data-testid={`response-${r.id}`}>
                    <div>
                      <p className="font-medium text-zinc-800">{r.facility_name}</p>
                      {r.denial_reason && <p className="text-xs text-red-600">No — {r.denial_reason}</p>}
                    </div>
                    <div className="flex items-center gap-3">
                      <StatusBadge status={r.response_status} />
                      {(isFacility || isCC) && r.response_status === "Pending" &&
                        <Button size="sm" onClick={() => { setRespondFor(r); setResp({ response_status: "Accepted", proposed_start_date: pr.requested_start_date, proposed_end_date: pr.requested_end_date }); }} className="bg-cc-blue text-white" data-testid={`respond-${r.id}`}>Respond</Button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="paperwork">
          <Card>
            {isCC && (
              <div className="flex justify-end gap-2 mb-3">
                <Button size="sm" variant="outline" onClick={() => setFilesOpen(true)} data-testid="open-sharepoint-folder-btn"><FolderOpen className="w-4 h-4 mr-1" />Student files</Button>
                <Button size="sm" variant="outline" onClick={() => setAdhocOpen(true)} data-testid="add-adhoc-paperwork-btn">+ Add Document</Button>
              </div>
            )}
            {pr.paperwork?.length === 0 ? (
              <div className="text-center py-10 space-y-3">
                <p className="text-sm text-zinc-500">No documents assigned yet. The packet is assigned automatically when a facility says Yes.</p>
                {isCC && (
                  <Button size="sm" className="bg-cc-blue text-white" onClick={assignPacket} data-testid="assign-packet-btn">Assign document packet now</Button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {pr.paperwork.map((a) => (
                  <div key={a.id} className="flex items-center justify-between border rounded-md p-3" data-testid={`paperwork-${a.id}`}>
                    <div>
                      <p className="font-medium text-zinc-800 text-sm">{a.name}
                        {a.ad_hoc && <span className="ml-2 text-[10px] uppercase tracking-wide text-cc-blue border border-cc-blue/30 rounded px-1.5 py-0.5">Added</span>}
                        <span className={`ml-2 text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 ${a.required ? "text-amber-700 bg-amber-50" : "text-zinc-400 bg-zinc-100"}`}>{a.required ? "Required" : "Optional"}</span>
                        <span className="ml-2 text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 text-zinc-500 bg-zinc-100">{a.doc_type === "upload" ? "Upload" : "Form"}</span>
                      </p>
                      <p className="text-xs text-zinc-400">Due {fmt(a.due_date)}{a.file_name ? ` · 📎 ${a.file_name}` : ""}{a.rejection_reason ? ` · ${a.rejection_reason}` : ""}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {a.has_template && <a href={`${process.env.REACT_APP_BACKEND_URL}/api/paperwork-assignments/${a.id}/template`} target="_blank" rel="noreferrer" className="text-cc-blue text-xs" data-testid={`download-blank-${a.id}`}>Blank form</a>}
                      {isCC && a.doc_type !== "upload" && <AttachBlankBtn assignmentId={a.id} hasTemplate={a.has_template} onChange={load} />}
                      {a.file_location && <a href={`${process.env.REACT_APP_BACKEND_URL}/api/paperwork-assignments/${a.id}/file`} target="_blank" rel="noreferrer" className="text-cc-blue text-xs" data-testid={`view-pw-${a.id}`}>View</a>}
                      <StatusBadge status={a.status} />
                      {isStudent && ["Assigned", "Rejected", "In Progress", "Not Started"].includes(a.status) && (
                        <label className="inline-flex">
                          <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => submitPaperwork(a, e.target.files?.[0])} data-testid={`submit-pw-${a.id}`} />
                          <span className="cursor-pointer text-xs px-3 py-1.5 border rounded-md hover:bg-zinc-50">Upload &amp; Submit</span>
                        </label>
                      )}
                      {isCC && ["Submitted", "Pending Review"].includes(a.status) && (
                        <>
                          <Button size="sm" className="bg-cc-green text-white" onClick={() => reviewPaperwork(a, "Approved")} data-testid={`approve-pw-${a.id}`}><CheckCircle2 className="w-4 h-4" /></Button>
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => reviewPaperwork(a, "Rejected", "Illegible / needs resubmission")} data-testid={`reject-pw-${a.id}`}><XCircle className="w-4 h-4" /></Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="messages">
          <Card>
            <div className="space-y-3 max-h-96 overflow-y-auto cc-scroll mb-4">
              {pr.messages?.length === 0 ? <EmptyState text="No messages yet." /> :
                pr.messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender_user_id === user.id ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[70%] rounded-lg px-3 py-2 text-sm ${m.sender_user_id === user.id ? "bg-cc-blue text-white" : "bg-zinc-100"}`}>
                      <p className="text-[10px] opacity-70 mb-0.5">{m.sender_name}</p>{m.message_body}
                    </div>
                  </div>
                ))}
            </div>
            <div className="flex gap-2">
              <Input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Type a message…" onKeyDown={(e) => e.key === "Enter" && sendMsg()} data-testid="message-input" />
              <Button onClick={sendMsg} className="bg-cc-blue text-white" data-testid="send-message-btn"><Send className="w-4 h-4" /></Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="timeline">
          <Card>
            <div className="space-y-3">
              {pr.timeline?.map((t) => (
                <div key={t.id} className="flex gap-3 text-sm">
                  <div className="w-2 h-2 rounded-full bg-cc-blue mt-1.5 shrink-0" />
                  <div>
                    <p className="text-zinc-800">{t.action.replace(/_/g, " ")} {t.new_value ? `→ ${JSON.stringify(t.new_value)}` : ""}</p>
                    <p className="text-xs text-zinc-400">{t.user_name} · {new Date(t.created_at).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Assign facility dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Assign Facility</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-500 mb-2">Select the specific facility the school requested. We'll pull the DOR, Area Manager &amp; address, then email the DOR to mark Yes/No.</p>
          <Field label="Facility">
            <Popover open={facPickerOpen} onOpenChange={setFacPickerOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" data-testid="assign-facility-select" className="w-full justify-between font-normal">
                  {assignFacilityId ? (matches.find((f) => f.id === assignFacilityId)?.name || "Selected") : <span className="text-zinc-400">Search facilities…</span>}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command filter={(value, search) => (value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}>
                  <CommandInput placeholder="Search by facility name…" data-testid="assign-facility-search" />
                  <CommandList>
                    <CommandEmpty>No facility found.</CommandEmpty>
                    <CommandGroup>
                      {matches.map((f) => (
                        <CommandItem key={f.id} value={`${f.name} ${f.state || ""} ${f.client || ""}`}
                          onSelect={() => { setAssignFacilityId(f.id); setFacPickerOpen(false); }} data-testid={`assign-opt-${f.id}`}>
                          <div className="flex-1">
                            <p className="text-sm">{f.name}</p>
                            <p className="text-xs text-zinc-400">{[f.city, f.state, f.client].filter(Boolean).join(" · ")}{f.dor_email ? "" : " · no DOR email"}</p>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </Field>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)}>Cancel</Button>
            <Button onClick={doAssign} className="bg-cc-blue text-white" data-testid="confirm-assign-btn">Assign &amp; Email DOR</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel placement dialog */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Cancel this placement</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-500 mb-2">Add a short reason so everyone involved (student, school, facility &amp; coordinator) sees why it was cancelled. They'll all be notified.</p>
          <Field label="Reason"><Textarea data-testid="cancel-reason" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="e.g. Student withdrew from the program" /></Field>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)}>Keep placement</Button>
            <Button onClick={doCancel} className="bg-red-600 text-white" data-testid="confirm-cancel-btn">Cancel &amp; notify everyone</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Final No dialog */}
      <Dialog open={finalNoOpen} onOpenChange={setFinalNoOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Campus Coordinator Final No</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-500 mb-2">Confirm there are no other local opportunities. The school contact will be notified.</p>
          <Field label="Reason"><Textarea data-testid="final-no-reason" value={finalNoReason} onChange={(e) => setFinalNoReason(e.target.value)} placeholder="e.g. All local facilities declined or unavailable" /></Field>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFinalNoOpen(false)}>Cancel</Button>
            <Button onClick={doFinalNo} className="bg-red-600 text-white" data-testid="confirm-final-no-btn">Confirm Final No</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add ad-hoc document dialog */}
      <Dialog open={adhocOpen} onOpenChange={setAdhocOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add a document to this packet</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-500 mb-2">Attach a one-off document for this student mid-rotation. Required documents must be approved before the student can be cleared to start.</p>
          <div className="space-y-3">
            <Field label="Document name"><Input data-testid="adhoc-name" value={adhoc.name} onChange={(e) => setAdhoc({ ...adhoc, name: e.target.value })} placeholder="e.g. Updated TB Test" /></Field>
            <Field label="Description (optional)"><Textarea data-testid="adhoc-description" value={adhoc.description} onChange={(e) => setAdhoc({ ...adhoc, description: e.target.value })} /></Field>
            <Field label="Due date (optional)"><Input type="date" data-testid="adhoc-due" value={adhoc.due_date} onChange={(e) => setAdhoc({ ...adhoc, due_date: e.target.value })} /></Field>
            <Field label="Document type">
              <Select value={adhoc.doc_type} onValueChange={(v) => setAdhoc({ ...adhoc, doc_type: v })}>
                <SelectTrigger data-testid="adhoc-doc-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="form">Fill-out form (you attach a blank for them)</SelectItem>
                  <SelectItem value="upload">Upload document (student uploads their own)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={adhoc.required} onCheckedChange={(c) => setAdhoc({ ...adhoc, required: !!c })} data-testid="adhoc-required" />
              Required for clearance (blocks "Clear to Start" until approved)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdhocOpen(false)}>Cancel</Button>
            <Button onClick={addAdhoc} disabled={adhocSaving} className="bg-cc-blue text-white" data-testid="confirm-adhoc-btn">{adhocSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Document"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Match dialog */}
      <Dialog open={matchOpen} onOpenChange={setMatchOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Matched Facilities</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto cc-scroll">
            {matches.length === 0 ? <EmptyState text="No matching facilities." /> : matches.map((f) => (
              <label key={f.id} className="flex items-center gap-3 border rounded-md p-3 cursor-pointer" data-testid={`match-${f.id}`}>
                <Checkbox checked={!!selected[f.id]} onCheckedChange={(c) => setSelected({ ...selected, [f.id]: c })} />
                <div className="flex-1">
                  <p className="font-medium text-sm">{f.name}</p>
                  <p className="text-xs text-zinc-400">{f.city}, {f.state} · {f.available_slots} slots</p>
                </div>
                {f.over_capacity && <StatusBadge status="Over Capacity" />}
              </label>
            ))}
          </div>
          <DialogFooter><Button onClick={notify} className="bg-cc-blue text-white" data-testid="notify-facilities-btn">Notify Selected</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Respond dialog */}
      <Dialog open={!!respondFor} onOpenChange={(o) => !o && setRespondFor(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto cc-scroll">
          <DialogHeader><DialogTitle>Facility Response — {respondFor?.facility_name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Can your facility accept this student?">
              <Select value={resp.response_status} onValueChange={(v) => setResp({ ...resp, response_status: v })}>
                <SelectTrigger data-testid="response-decision"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Accepted">Yes — we can accept</SelectItem>
                  <SelectItem value="Denied">No — we can't take this student</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {resp.response_status === "Accepted" && (
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Field label="Assign Clinical Instructor *">
                    <Select value={resp.clinical_instructor_id || ""} onValueChange={(v) => setResp({ ...resp, clinical_instructor_id: v })}>
                      <SelectTrigger data-testid="assign-ci-select"><SelectValue placeholder={facilityCIs.length ? "Select the supervising instructor" : "No instructors yet — add one below"} /></SelectTrigger>
                      <SelectContent>{facilityCIs.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.role_label ? ` (${c.role_label})` : ""}{c.disciplines?.length ? ` · ${c.disciplines.join("/")}` : ""}{c.email ? ` · ${c.email}` : ""}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                </div>
                <div className="col-span-2">
                  {!addingCi ? (
                    <Button type="button" variant="outline" size="sm" onClick={() => setAddingCi(true)} data-testid="add-ci-toggle">+ Add a new Clinical Instructor</Button>
                  ) : (
                    <div className="border rounded-md p-3 space-y-2 bg-zinc-50">
                      <div className="grid grid-cols-2 gap-2">
                        <Input placeholder="Name" value={newCi.name || ""} onChange={(e) => setNewCi({ ...newCi, name: e.target.value })} data-testid="new-ci-name" />
                        <Input placeholder="Email" value={newCi.email || ""} onChange={(e) => setNewCi({ ...newCi, email: e.target.value })} data-testid="new-ci-email" />
                        <Input placeholder="Phone" value={newCi.phone || ""} onChange={(e) => setNewCi({ ...newCi, phone: e.target.value })} data-testid="new-ci-phone" />
                        <Select value={newCi.discipline || ""} onValueChange={(v) => setNewCi({ ...newCi, discipline: v })}>
                          <SelectTrigger data-testid="new-ci-discipline"><SelectValue placeholder="Discipline" /></SelectTrigger>
                          <SelectContent>{CI_DISCIPLINES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="flex gap-2">
                        <Button type="button" size="sm" className="bg-cc-blue text-white" onClick={addNewCi} data-testid="new-ci-save">Add instructor</Button>
                        <Button type="button" size="sm" variant="ghost" onClick={() => setAddingCi(false)}>Cancel</Button>
                      </div>
                    </div>
                  )}
                </div>
                <Field label="Rotation start date"><Input type="date" value={resp.proposed_start_date || ""} onChange={(e) => setResp({ ...resp, proposed_start_date: e.target.value })} data-testid="resp-start" /></Field>
                <Field label="Rotation end date"><Input type="date" value={resp.proposed_end_date || ""} onChange={(e) => setResp({ ...resp, proposed_end_date: e.target.value })} data-testid="resp-end" /></Field>
              </div>
            )}
            {resp.response_status === "Denied" && (
              <>
                <Field label="Reason">
                  <Select value={resp.denial_reason} onValueChange={(v) => setResp({ ...resp, denial_reason: v })}>
                    <SelectTrigger data-testid="denial-reason"><SelectValue placeholder="Select reason" /></SelectTrigger>
                    <SelectContent>{denialReasons.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Details"><Textarea onChange={(e) => setResp({ ...resp, denial_notes: e.target.value })} /></Field>
                <p className="text-xs text-zinc-400">Marking No sends this back to the Campus Coordinator to try another site before the school is notified.</p>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRespondFor(null)}>Cancel</Button>
            <Button onClick={submitResponse} disabled={(resp.response_status === "Accepted" && !resp.clinical_instructor_id) || (resp.response_status === "Denied" && !resp.denial_reason)} className="bg-cc-blue text-white" data-testid="submit-response-btn">{resp.response_status === "Accepted" ? "Confirm Yes" : "Confirm No"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <StudentFiles prId={id} open={filesOpen} onClose={() => setFilesOpen(false)} />
    </div>
  );
}

function ProgramDocuments({ prId, role }) {
  const canAdd = ["Super Admin", "Campus Coordinator", "Campus Coordinator Supervisor", "School User"].includes(role);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ title: "", url: "" });
  const load = () => api.get(`/placement-requests/${prId}/program-documents`).then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [prId]);
  const add = async () => {
    try {
      await api.post(`/placement-requests/${prId}/program-documents`, form);
      setForm({ title: "", url: "" }); toast.success("Document shared with the Clinical Instructor"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (d) => {
    try { await api.delete(`/program-documents/${d.id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Card title="Program documents" data-testid="program-documents-card">
      <p className="text-xs text-zinc-500 mb-3">
        Paperwork the school sends with this student — evaluation forms, objectives, the confidentiality form.
        Paste a SharePoint or OneDrive link; the Clinical Instructor sees them on their dashboard.
      </p>
      {rows.length === 0 ? <EmptyState text="Nothing shared yet." /> : (
        <div className="space-y-1.5 mb-3">
          {rows.map((d) => (
            <div key={d.id} className="flex items-center gap-2 border rounded-md px-3 py-2" data-testid={`progdoc-${d.id}`}>
              <a href={d.url} target="_blank" rel="noreferrer" className="text-sm text-cc-blue hover:underline flex-1 truncate">{d.title}</a>
              <span className="text-xs text-zinc-400 shrink-0">{d.added_by_name}</span>
              {canAdd && <button onClick={() => remove(d)} className="text-zinc-400 hover:text-red-600" data-testid={`progdoc-delete-${d.id}`}><Trash2 className="w-4 h-4" /></button>}
            </div>
          ))}
        </div>
      )}
      {canAdd && (
        <div className="flex flex-wrap gap-2">
          <Input className="w-48" placeholder="Document name" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="progdoc-title" />
          <Input className="flex-1 min-w-[14rem]" placeholder="https://therapymgmt.sharepoint.com/…" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="progdoc-url" />
          <Button onClick={add} disabled={!form.title.trim() || !form.url.trim()} className="bg-cc-blue text-white" data-testid="progdoc-add">Share</Button>
        </div>
      )}
    </Card>
  );
}

function Info({ label, value }) {
  return <div><div className="text-xs uppercase tracking-wide text-zinc-400">{label}</div><div className="text-zinc-800 break-words">{value || "—"}</div></div>;
}

// Student contact details on the placement itself, so coordinators don't have to go to Users.
function StudentContact({ pr, isDOR, canOpenProfile }) {
  const s = pr.student || {};
  const cleared = pr.clearance_status === "Cleared to Start";
  const name = s.name || pr.student_name_text || "Student";
  const personal = s.personal_email || pr.student_personal_email;
  const school = s.school_email || pr.student_school_email;
  const cell = s.cell_phone || pr.student_cell_phone;
  const location = [s.address_city, s.address_state, s.address_zip].filter(Boolean).join(", ");
  if (isDOR && !cleared) {
    return (
      <Card title="Student contact" data-testid="student-contact-card">
        <EmptyState text="Contact details are shared with the facility once Campus Relations marks the student All Clear." />
      </Card>
    );
  }
  return (
    <Card title="Student contact" data-testid="student-contact-card"
      action={canOpenProfile && pr.student_id ? (
        <Link to={`/students/${pr.student_id}`} className="text-sm text-cc-blue hover:underline inline-flex items-center gap-1" data-testid="student-contact-profile-link">
          <IdCard className="w-4 h-4" />Full profile
        </Link>
      ) : null}>
      <div className="grid md:grid-cols-2 gap-x-8 gap-y-4 text-sm">
        <Info label="Name" value={name} />
        <Info label="Discipline" value={s.discipline || pr.discipline} />
        <Info label="Personal email" value={personal} />
        <Info label="School email" value={school} />
        <Info label="Cell" value={cell} />
        <Info label="Location" value={location} />
        <Info label="Street" value={s.address_street} />
        <Info label="Graduation" value={fmtDate(s.graduation_date || pr.graduation_date)} />
        <Info label="Emergency contact" value={s.emergency_contact_name} />
        <Info label="Emergency phone" value={s.emergency_contact_phone} />
        <Info label="School" value={pr.school?.name} />
        <Info label="Clinical Educator" value={[pr.clinical_educator_name, pr.clinical_educator_email].filter(Boolean).join(" · ")} />
      </div>
      {!personal && !school && !cell && (
        <p className="mt-4 text-xs text-zinc-500" data-testid="student-contact-empty-note">
          No contact details on file yet — add them on the student's full profile.
        </p>
      )}
    </Card>
  );
}

function AttachBlankBtn({ assignmentId, hasTemplate, onChange }) {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/paperwork-assignments/${assignmentId}/template`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Blank form attached"); onChange?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not attach the form"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <label className="inline-flex items-center gap-1 text-xs text-zinc-500 cursor-pointer hover:text-cc-blue" data-testid={`attach-blank-${assignmentId}`}>
      <input type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx" onChange={onFile} />
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (hasTemplate ? "Replace blank" : "Attach blank")}
    </label>
  );
}
const fmt = fmtDate;
